
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface FunnelStage {
  label: string;
  value: number;
  count: number;
  color: string;
}

interface FunnelFlowProps {
  stages: FunnelStage[];
  width?: number;
  height?: number;
}

const FunnelFlow = ({ 
  stages, 
  width = 800, 
  height = 400 
}: FunnelFlowProps) => {
  const padding = { top: 40, bottom: 40, left: 60, right: 60 };
  const availableHeight = height - padding.top - padding.bottom;
  const availableWidth = width - padding.left - padding.right;
  const svgWidth = availableWidth * 0.6; // Reduce SVG width to make room for table
  
  // Calculate stage positions and dimensions
  const stageHeight = availableHeight / stages.length;
  const maxWidth = svgWidth * 0.8; // Max width of the funnel
  
  // Find the maximum value to normalize widths
  const maxValue = Math.max(...stages.map(s => s.count));
  
  const getStageWidth = (value: number) => {
    return (value / maxValue) * maxWidth;
  };

  const createFlowPath = (startWidth: number, endWidth: number, y: number) => {
    const startX = (svgWidth - startWidth) / 2 + padding.left;
    const endX = (svgWidth - endWidth) / 2 + padding.left;
    const controlPointY = stageHeight * 0.5;

    return `
      M ${startX} ${y}
      L ${startX + startWidth} ${y}
      C ${startX + startWidth} ${y + controlPointY} 
        ${endX + endWidth} ${y + stageHeight - controlPointY} 
        ${endX + endWidth} ${y + stageHeight}
      L ${endX} ${y + stageHeight}
      C ${endX} ${y + stageHeight - controlPointY} 
        ${startX} ${y + controlPointY} 
        ${startX} ${y}
      Z
    `;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex gap-8">
          <div className="w-3/5" style={{ height }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${width * 0.6} ${height}`} preserveAspectRatio="xMidYMid meet">
              <g>
                {stages.map((stage, index) => {
                  const currentWidth = getStageWidth(stage.count);
                  const nextStage = stages[index + 1];
                  const nextWidth = nextStage ? getStageWidth(nextStage.count) : currentWidth;
                  const y = index * stageHeight + padding.top;

                  return (
                    <g key={stage.label}>
                      <path
                        d={createFlowPath(currentWidth, nextWidth, y)}
                        fill={stage.color}
                        opacity={0.8}
                      />
                      
                      {/* Stage Label */}
                      <text
                        x={padding.left - 10}
                        y={y + stageHeight / 2}
                        textAnchor="end"
                        alignmentBaseline="middle"
                        className="text-sm fill-muted-foreground"
                      >
                        {stage.label}
                      </text>
                      
                      {/* Value */}
                      <text
                        x={(svgWidth / 2) + padding.left}
                        y={y + stageHeight / 2}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        className="text-base font-medium fill-foreground"
                      >
                        {stage.count}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          <div className="w-2/5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stage</TableHead>
                  <TableHead>Count</TableHead>
                  <TableHead>Conv. Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stages.map((stage, index) => (
                  <TableRow key={stage.label}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: stage.color }}
                        />
                        {stage.label}
                      </div>
                    </TableCell>
                    <TableCell>{stage.count}</TableCell>
                    <TableCell>{stage.value}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FunnelFlow;

